import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Alert, Box, Button, Dialog,
    DialogActions, DialogContent,
    DialogTitle, LinearProgress,
    Popover, Stack, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {changeFs as createAnnualFs, changeStatus} from '@/services/auditService/auditFsAnnualService';
import {exists as fsAnnualExists} from '@/services/auditService/auditFsAnnualService';
import {AnnualFsStatusEnum} from '@/services/auditService/annualFsStatusEnum';
import {
    commitFsCreateTx,
    ensureFsCreateTxStarted,
    rollbackFsCreateTx,
    activateFsCreateSeataFlow,
    clearFsCreateTxLocal,
} from '@/services/auditService/fsCreateTxService';
import {getSeataTxXid, SEATA_WIZARD_FLOW} from '@/services/seataTxHolder';
import {deleteCompany} from '@/services/accountService/companyService';
import {PopoverButton} from '@/pages/components/common';
import {FsCreateProvider, useFsCreateCtx} from '@/pages/components/audit/finance-statement/create-stepper/context';
import {
    StepACompany,
    StepBCaBasicInfo,
    StepCCaAuditConfig,
    StepDFinancialStatement,
} from '@/pages/components/audit/finance-statement/create-stepper';

/**
 * Seata AT rollback can leave account_company committed while audit branches undo
 * (see IAuditCompanyFSCategoryService#ensureCompanyFsCategories). Compensating delete
 * must run AFTER xid is cleared so soft-delete is not undone by the same global TX.
 *
 * Judgment: only delete when wizardCreatedCompanyId is set (company created in this
 * wizard). Queried/selected existing companies pass null → Seata rollback ignores company.
 */
async function rollbackFsCreateAndDropWizardCompany(options: {
    xid?: string | null;
    skipRollback?: boolean;
    /** Only wizard-created company id; null/undefined = ignore company on cancel */
    wizardCreatedCompanyId?: number | null;
}): Promise<void> {
    const xid = options.xid ?? getSeataTxXid(SEATA_WIZARD_FLOW.FS_CREATE);
    if (!options.skipRollback && xid) {
        try {
            await rollbackFsCreateTx(xid);
        } catch (e) {
            console.error('Seata rollback failed:', e);
        }
    }
    clearFsCreateTxLocal();
    const companyId = options.wizardCreatedCompanyId;
    if (companyId != null && companyId > 0) {
        try {
            await deleteCompany(companyId);
        } catch (e) {
            console.error('Failed to delete wizard-created company after rollback:', e);
        }
    }
}

export interface FsCreateStepperProps {
    open?: boolean;
    onCancel?: () => void;
    onCreated?: (message?: string) => void;
    // 'BS' for Balance Sheet, 'PL' for Profit & Loss
    statement?: 'BS' | 'PL';
    onOpenDetail?: (params: { companyId: number; annualId: number; companyName: string | null }) => void;
}

function FsCreateStepperInner({onCancel, onCreated, statement = 'BS', onOpenDetail}: FsCreateStepperProps) {
    const {state, dispatch, fileUploadActionRef, crValidateRef} = useFsCreateCtx();
    const activeStep = state.activeStep;
    const [crValidateMatched, setCrValidateMatched] = useState(false);
    const [crValidating, setCrValidating] = useState(false);
    const validateBtnRef = useRef<HTMLButtonElement>(null);

    // Excel 重新导入后重置 Validate 成功态
    useEffect(() => {
        setCrValidateMatched(false);
    }, [state.contentData]);

    /** null when company came from query/select — cancel must not delete it */
    const wizardCreatedCompanyId = state.wizardCreatedCompanyId;

    /** Snapshot for unmount abandon — commit path clears holder xid first so this will not delete. */
    const abandonCleanupRef = useRef<{wizardCreatedCompanyId: number | null}>({wizardCreatedCompanyId});
    abandonCleanupRef.current = {wizardCreatedCompanyId};

    const setActiveStep = useCallback((value: number | ((prev: number) => number)) => {
        const next = typeof value === 'function' ? value(state.activeStep) : value;
        dispatch({type: 'set_active_step', payload: next});
    }, [dispatch, state.activeStep]);

    const handleCancelAll = useCallback(async (options?: { skipRollback?: boolean }) => {
        const dropWizardCompany = !options?.skipRollback ? wizardCreatedCompanyId : null;
        await rollbackFsCreateAndDropWizardCompany({
            xid: state.seataXid,
            skipRollback: options?.skipRollback,
            wizardCreatedCompanyId: dropWizardCompany,
        });
        dispatch({type: 'set_seata_xid', payload: null});
        dispatch({type: 'clear_draft'});
        dispatch({type: 'set_active_step', payload: 0});
        onCancel?.();
    }, [dispatch, onCancel, state.seataXid, wizardCreatedCompanyId]);

    useEffect(() => {
        return () => {
            const snap = abandonCleanupRef.current;
            // Holder only: Dialog/Cancel clear xid before unmount so we do not double-delete.
            const xid = getSeataTxXid(SEATA_WIZARD_FLOW.FS_CREATE);
            if (xid) {
                void rollbackFsCreateAndDropWizardCompany({
                    xid,
                    wizardCreatedCompanyId: snap.wizardCreatedCompanyId,
                });
            } else {
                clearFsCreateTxLocal();
            }
            dispatch({type: 'clear_draft'});
            dispatch({type: 'set_active_step', payload: 0});
            dispatch({type: 'set_seata_xid', payload: null});
        };
    }, [dispatch]);

    const ensureTx = useCallback(async (): Promise<boolean> => {
        try {
            const xid = await ensureFsCreateTxStarted();
            dispatch({type: 'set_seata_xid', payload: xid});
            return true;
        } catch (e: any) {
            setSubmitError(e?.message || 'Failed to start transaction');
            return false;
        }
    }, [dispatch]);

    const canNextFromStep1 = !!state.selectedCompany || !!state.beforeNext;
    const canNextFromStep2 = !!state.selectedAnnual || (state.isCreatingAnnual && !!state.annualId);
    const canNextFromStep3 = !!state.annualId;

    const [existsDialogOpen, setExistsDialogOpen] = useState(false);
    const [existsTarget, setExistsTarget] = useState<{ companyId: number; annualId: number } | null>(null);
    const [existsChecking, setExistsChecking] = useState(false);
    const [assessableYearRollbackLoading, setAssessableYearRollbackLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [updating, setUpdating] = useState(false);
    const [submitAnchorEl, setSubmitAnchorEl] = useState<HTMLElement | null>(null);

    const {companyId, annualId} = useMemo(() => {
        const rawCompanyId = (state.selectedCompany as any)?.companyId ?? (state.selectedCompany as any)?.id;
        const rawAnnualId = state.annualId ?? (state.selectedAnnual as any)?.annualId ?? (state.selectedAnnual as any)?.id;
        return {
            companyId: rawCompanyId != null ? Number(rawCompanyId) : null,
            annualId: rawAnnualId != null ? Number(rawAnnualId) : null,
        } as { companyId: number | null; annualId: number | null };
    }, [state.selectedCompany, state.selectedAnnual, state.annualId]);

    const checkExists = useCallback(async (): Promise<boolean> => {
        if (!companyId || !annualId) return false;
        try {
            setExistsChecking(true);
            const exists = await fsAnnualExists(companyId, annualId);
            if (exists) {
                setExistsTarget({companyId, annualId});
                setExistsDialogOpen(true);
                return true;
            }
            return false;
        } catch {
            return false;
        } finally {
            setExistsChecking(false);
        }
    }, [companyId, annualId]);

    const handleBack = useCallback(async () => {
        if (activeStep === 1 && state.isCreatingAnnual) {
            const currentCompanyId = (state.selectedCompany as any)?.companyId ?? (state.selectedCompany as any)?.id;
            const rawFormData = state.getFormData ? state.getFormData() : null;
            const {annualId: _draftAnnualId, ...formData} = (rawFormData as any) || {};

            dispatch({
                type: 'save_draft',
                payload: {
                    companyId: currentCompanyId,
                    annualId: null,
                    formData,
                },
            });
            dispatch({type: 'set_is_creating_annual', payload: false});
        }

        if (activeStep === 1 && state.companyCreatedViaWizard) {
            dispatch({type: 'set_company_edit_on_step0', payload: true});
        }

        if (state.beforeNext) {
            dispatch({type: 'set_is_processing', payload: true});
            try {
                await state.beforeNext(false);
            } catch (e) {
                console.error('Save before back failed:', e);
            } finally {
                dispatch({type: 'set_is_processing', payload: false});
            }
        }

        setActiveStep((s) => Math.max(0, s - 1));
    }, [activeStep, state.beforeNext, state.isCreatingAnnual, state.companyCreatedViaWizard, state.selectedCompany, state.getFormData, dispatch, setActiveStep]);

    const handleNext = useCallback(async () => {
        if (!(await ensureTx())) {
            return;
        }

        if (state.beforeNext) {
            dispatch({type: 'set_is_processing', payload: true});
            try {
                const canProceed = await state.beforeNext();
                if (!canProceed) {
                    dispatch({type: 'set_is_processing', payload: false});
                    return;
                }
            } catch (e) {
                console.error('Before next failed:', e);
                dispatch({type: 'set_is_processing', payload: false});
                return;
            } finally {
                dispatch({type: 'set_is_processing', payload: false});
            }
        }

        setActiveStep((s) => Math.min(3, s + 1));
    }, [ensureTx, state.beforeNext, dispatch, setActiveStep]);

    const handleUpdate = useCallback(async () => {
        if (!companyId || !annualId) return;
        dispatch({type: 'set_is_processing', payload: true});
        setUpdating(true);
        try {
            setSubmitError(null);
            const payloadFromDetail = state.getLines?.();
            const bsLines = payloadFromDetail?.bsLines || [];
            const plLines = payloadFromDetail?.plLines || [];
            await createAnnualFs({companyId, annualId, balanceSheetLines: bsLines, profitLossLines: plLines});
            await commitFsCreateTx(state.seataXid);
            dispatch({type: 'set_seata_xid', payload: null});
            onCreated?.('Updated successfully');
        } catch (e: any) {
            setSubmitError(e?.message || 'Update failed');
        } finally {
            setUpdating(false);
            dispatch({type: 'set_is_processing', payload: false});
        }
    }, [companyId, annualId, dispatch, onCreated, state.getLines, state.seataXid]);

    const handleConfirmSubmit = useCallback(async () => {
        if (!companyId || !annualId) return;
        dispatch({type: 'set_is_processing', payload: true});
        setUpdating(true);
        try {
            setSubmitError(null);
            const payloadFromDetail = state.getLines?.();
            const bsLines = payloadFromDetail?.bsLines || [];
            const plLines = payloadFromDetail?.plLines || [];
            await createAnnualFs({companyId, annualId, balanceSheetLines: bsLines, profitLossLines: plLines});
            await changeStatus(companyId, annualId, AnnualFsStatusEnum.AUDIT_ADJUSTMENT);
            await commitFsCreateTx(state.seataXid);
            dispatch({type: 'set_seata_xid', payload: null});
            setSubmitAnchorEl(null);
            onCreated?.('Submitted successfully');
        } catch (e: any) {
            setSubmitError(e?.message || 'Submit failed');
        } finally {
            setUpdating(false);
            dispatch({type: 'set_is_processing', payload: false});
        }
    }, [companyId, annualId, dispatch, onCreated, state.getLines, state.seataXid]);

    const handleExistsNo = useCallback(() => {
        setExistsDialogOpen(false);
        void handleCancelAll();
    }, [handleCancelAll]);

    const handleExistsYes = useCallback(() => {
        const target = existsTarget;
        setExistsDialogOpen(false);
        if (target) {
            void handleCancelAll({skipRollback: true});
            onOpenDetail?.({
                companyId: target.companyId,
                annualId: target.annualId,
                companyName: (state.selectedCompany as any)?.companyName || null
            });
        }
    }, [existsTarget, handleCancelAll, onOpenDetail, state.selectedCompany]);

    const handleAssessableYearConflictConfirm = useCallback(async () => {
        setAssessableYearRollbackLoading(true);
        try {
            dispatch({type: 'set_assessable_year_conflict', payload: null});
            await handleCancelAll();
        } finally {
            setAssessableYearRollbackLoading(false);
        }
    }, [dispatch, handleCancelAll]);

    const linearValue = useMemo(() => ((activeStep + 1) / 4) * 100, [activeStep]);

    const showStepUploadFooter = activeStep === 0 && state.uploadPanelOpen;

    const stepComponents = useMemo<React.ReactNode[]>(() => [
        <StepACompany key="step-a"/>,
        <StepBCaBasicInfo key="step-b"/>,
        <StepCCaAuditConfig key="step-c"/>,
        <StepDFinancialStatement key="step-d"/>,
    ], []);

    const nextDisabled = useMemo(() => {
        if (state.isSaving || state.isProcessing || existsChecking) return true;
        if (activeStep === 0) return !canNextFromStep1;
        // Step 1/2: Next enabled; confirm checkbox validates on click (EpPeriod.confirmValidateRef)
        return activeStep > 2;
    }, [activeStep, canNextFromStep1, existsChecking, state.isSaving, state.isProcessing]);

    return (
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0}}>
            <Box sx={{width: '100%', px: 2, mx: 'auto', mb: 1}}>
                <Stack spacing={0.5}>
                    <LinearProgress variant="determinate" value={linearValue}/>
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color={activeStep >= 0 ? 'text.primary' : 'text.disabled'}>
                            Company Information
                        </Typography>
                        <Typography variant="caption" color={activeStep >= 1 ? 'text.primary' : 'text.disabled'}>
                            Annual Basic Info
                        </Typography>
                        <Typography variant="caption" color={activeStep >= 2 ? 'text.primary' : 'text.disabled'}>
                            Annual Audit Config
                        </Typography>
                        <Typography variant="caption" color={activeStep >= 3 ? 'text.primary' : 'text.disabled'}>
                            Financial Statement
                        </Typography>
                    </Stack>
                </Stack>
            </Box>

            {submitError && (
                <Box sx={{width: '100%', px: 2, mx: 'auto', mb: 1}}>
                    <Alert severity="error">{submitError}</Alert>
                </Box>
            )}

            <Box sx={{
                width: '100%',
                px: 2,
                mx: 'auto',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflowY: showStepUploadFooter ? 'hidden' : 'auto',
            }}>
                {stepComponents[activeStep]}
            </Box>

            <Stack direction="row" spacing={1} justifyContent="flex-end" mt={2}
                   sx={{width: '100%', px: 2, mx: 'auto', pt: 2, borderTop: 1, borderColor: 'divider'}}>
                {!state.contentData && !showStepUploadFooter && activeStep > 0 && (
                    <Button onClick={() => void handleCancelAll()}>Cancel</Button>
                )}
                <Box flex={1}/>
                {showStepUploadFooter ? (
                    <>
                        <Button
                            disabled={state.uploadUploading}
                            onClick={() => fileUploadActionRef.current?.cancel()}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disabled={!state.uploadCanSubmit || state.uploadUploading}
                            onClick={() => fileUploadActionRef.current?.upload()}
                        >
                            {state.uploadUploading ? 'Uploading...' : 'Upload'}
                        </Button>
                    </>
                ) : (
                    <>
                        {activeStep > 0 && (
                            <Button disabled={state.isProcessing} onClick={handleBack}>
                                {state.isProcessing ? 'Saving...' : 'Back'}
                            </Button>
                        )}
                        {activeStep === 0 && !!state.contentData && (
                            crValidateMatched ? (
                                <Alert
                                    severity="success"
                                    icon={<CheckCircleOutlineIcon fontSize="inherit"/>}
                                    sx={{py: 0, alignItems: 'center'}}
                                >
                                    Validate result: matched
                                </Alert>
                            ) : (
                                <Button
                                    ref={validateBtnRef}
                                    variant="outlined"
                                    disabled={crValidating || state.isProcessing}
                                    onClick={async () => {
                                        setCrValidating(true);
                                        try {
                                            const outcome = await crValidateRef.current?.validate(validateBtnRef.current) ?? 'error';
                                            if (outcome === 'matched') setCrValidateMatched(true);
                                        } finally {
                                            setCrValidating(false);
                                        }
                                    }}
                                >
                                    {crValidating ? 'Validating...' : 'Validate'}
                                </Button>
                            )
                        )}
                        {activeStep < 3 && (
                            <Button variant="contained" disabled={nextDisabled} onClick={handleNext}>
                                {state.isSaving ? 'Saving...' : state.isProcessing ? 'Processing...' : 'Next'}
                            </Button>
                        )}
                        {activeStep === 3 && (
                            <>
                                {state.bsUnbalanced ? (
                                    <Button
                                        variant="contained"
                                        disabled={state.isProcessing || updating}
                                        onClick={handleUpdate}
                                    >
                                        {state.isProcessing || updating ? 'Updating...' : 'Update'}
                                    </Button>
                                ) : !state.fsHideSubmit ? (
                                    <Button
                                        variant="outlined"
                                        disabled={state.isProcessing || updating}
                                        onClick={(e) => setSubmitAnchorEl(e.currentTarget)}
                                    >
                                        Submit
                                    </Button>
                                ) : null}
                            </>
                        )}
                    </>
                )}
            </Stack>

            <Popover
                open={Boolean(submitAnchorEl)}
                anchorEl={submitAnchorEl}
                onClose={() => setSubmitAnchorEl(null)}
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                transformOrigin={{vertical: 'bottom', horizontal: 'center'}}
                PaperProps={{sx: {p: 2, width: 420}}}
            >
                <Alert severity="warning" sx={{mb: 1}}>
                    Is the company's financial report entering the adjustment phase?
                </Alert>
                <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1}}>
                    <Button onClick={() => setSubmitAnchorEl(null)}>No</Button>
                    <Button variant="contained" disabled={state.isProcessing || updating} onClick={handleConfirmSubmit}>
                        Yes
                    </Button>
                </Box>
            </Popover>

            <Dialog open={existsDialogOpen} onClose={() => setExistsDialogOpen(false)}>
                <DialogTitle>Confirm</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">The company's annual report already exists. Do you want to open
                        it?</Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleExistsNo}>No</Button>
                    <Button variant="contained" onClick={handleExistsYes}>Yes</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!state.assessableYearConflict}
                onClose={() => { /* force user to acknowledge */ }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Warning</DialogTitle>
                <DialogContent>
                    <Alert severity="warning">{state.assessableYearConflict}</Alert>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        disabled={assessableYearRollbackLoading}
                        onClick={() => void handleAssessableYearConflictConfirm()}
                    >
                        {assessableYearRollbackLoading ? 'Processing...' : 'Confirm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

function FsCreateStepperDialog(props: FsCreateStepperProps) {
    const {onCancel} = props;
    const {state, dispatch} = useFsCreateCtx();
    const [rollbackLoading, setRollbackLoading] = useState(false);

    const showCloseConfirmation = useMemo(() => (
        state.activeStep > 0
        || state.isCreatingCompany
        || state.companyEditOnStep0
        || !!state.seataXid
    ), [state.activeStep, state.isCreatingCompany, state.companyEditOnStep0, state.seataXid]);

    const handleDirectClose = useCallback(() => {
        onCancel?.();
    }, [onCancel]);

    const handleConfirmRollbackClose = useCallback(async () => {
        setRollbackLoading(true);
        try {
            // Queried company → wizardCreatedCompanyId is null → only Seata rollback, no company delete
            await rollbackFsCreateAndDropWizardCompany({
                xid: state.seataXid,
                wizardCreatedCompanyId: state.wizardCreatedCompanyId,
            });
            dispatch({type: 'set_seata_xid', payload: null});
            dispatch({type: 'clear_draft'});
        } finally {
            setRollbackLoading(false);
            onCancel?.();
        }
    }, [dispatch, onCancel, state.seataXid, state.wizardCreatedCompanyId]);

    const closeTrigger = (
        <Button sx={{minWidth: 0, p: 0.5}} disabled={rollbackLoading}>
            <CloseIcon/>
        </Button>
    );

    return (
        <Dialog
            open={!!props.open}
            onClose={(_, reason) => {
                if (reason === 'backdropClick') return;
                if (showCloseConfirmation) return;
                handleDirectClose();
            }}
            disableEscapeKeyDown
            maxWidth="xl"
            fullWidth
            PaperProps={{sx: {width: '90vw', maxWidth: 1360}}}
        >
            <DialogTitle sx={{display: 'flex', alignItems: 'center'}}>
                Create Finance Statement
                {showCloseConfirmation ? (
                    <Box sx={{ml: 'auto'}}>
                        <PopoverButton
                            message="Please confirm to remove All Company Info?"
                            confirmLabel="Confirm"
                            onConfirm={handleConfirmRollbackClose}
                            loading={rollbackLoading}
                            trigger={closeTrigger}
                        />
                    </Box>
                ) : (
                    <Button onClick={handleDirectClose} sx={{ml: 'auto', minWidth: 0, p: 0.5}}>
                        <CloseIcon/>
                    </Button>
                )}
            </DialogTitle>
            <DialogContent dividers sx={{p: 2, display: 'flex', minHeight: 0}}>
                <Box sx={{flex: 1, display: 'flex', minHeight: 0}}>
                    <FsCreateStepperInner {...props} />
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export function FsCreateStepper(props: FsCreateStepperProps) {
    if (!props.open) return null;
    activateFsCreateSeataFlow();
    return (
        <FsCreateProvider>
            <FsCreateStepperDialog {...props} />
        </FsCreateProvider>
    );
}
